import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Switch,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RepeatIcon from '@mui/icons-material/Repeat';
import {
  getScheduleWindows,
  deleteScheduleWindow,
  toggleScheduleWindow,
} from '../api/client';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }) + ' UTC';
}

function formatTime(timeString) {
  if (!timeString) return '-';
  // timeString is like "09:00:00"
  const [hours, minutes] = timeString.split(':');
  return `${hours}:${minutes} UTC`;
}

function ScheduleWindowList({ pipelineId, onEdit }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getScheduleWindows(pipelineId);
      setSchedules(response.schedule_windows || []);
      setError('');
    } catch (err) {
      setError('Failed to load schedules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleToggle = async (scheduleId) => {
    try {
      await toggleScheduleWindow(scheduleId);
      fetchSchedules();
    } catch (err) {
      setError('Failed to toggle schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }
    try {
      await deleteScheduleWindow(scheduleId);
      fetchSchedules();
    } catch (err) {
      setError('Failed to delete schedule');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (schedules.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No schedules configured for this pipeline.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Schedule</TableCell>
            <TableCell>Next Start</TableCell>
            <TableCell>Next Stop</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {schedules.map((schedule) => (
            <TableRow key={schedule.id}>
              <TableCell>
                <Box>
                  <Typography variant="body2">{schedule.name}</Typography>
                  {schedule.description && (
                    <Typography variant="caption" color="text.secondary">
                      {schedule.description}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  icon={
                    schedule.schedule_type === 'one_time' ? (
                      <ScheduleIcon />
                    ) : (
                      <RepeatIcon />
                    )
                  }
                  label={schedule.schedule_type === 'one_time' ? 'One-Time' : 'Recurring'}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {schedule.schedule_type === 'one_time' ? (
                  <Box>
                    <Typography variant="caption" display="block">
                      Start: {formatDateTime(schedule.one_time_start)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      End: {formatDateTime(schedule.one_time_end)}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ mb: 0.5 }}>
                      {schedule.days_of_week?.map((d) => (
                        <Chip
                          key={d}
                          label={DAYS_OF_WEEK[d]}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" display="block">
                      {formatTime(schedule.recurring_start_time)} -{' '}
                      {formatTime(schedule.recurring_end_time)}
                    </Typography>
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(schedule.next_scheduled_start)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(schedule.next_scheduled_stop)}
                </Typography>
              </TableCell>
              <TableCell>
                <Switch
                  checked={schedule.is_active}
                  onChange={() => handleToggle(schedule.id)}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(schedule)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(schedule.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ScheduleWindowList;
